import Link from "next/link";
import PageTitle from "./pageTitle";
import { useTranslation } from "next-i18next";
import { useQuery } from "@apollo/client";
import { categoriesGql } from "../../gql/queries";
import { CategoryEntityResponseCollection } from "../../gql-types";
import Skeleton from "react-loading-skeleton";
import { formatCategories } from "helpers";
import { useCallback, useEffect, useState } from "react";
import _debounce from "lodash/debounce";
import TopStores from "./TopStores";
import { useWindowSize } from "helpers/scroll/animateScroll";
import BigCardThumbnail from "components/customImages/BigCardThumbnail";
import Image from "next/image";
import { categoryTranslateKey } from "../../helpers/constants/translate";
import { useGlobalState } from "../../context/GlobalStateContext";

const ARROW_ICON_SIZE = 70;
const CarouselCategories = ({ lang }) => {
  const size = useWindowSize();
  const { direction } = useGlobalState();
  const { loading, data } = useQuery<{
    categories?: CategoryEntityResponseCollection;
  }>(categoriesGql);
  const { t: translate } = useTranslation("categories_carousel");
  const { t: categoryTranslate } = useTranslation("categories");
  const [categories, setCategories] = useState([]);
  const [previousCarouselIndex, setPreviousCarouselIndex] = useState(0);
  const [imageWidth, setImageWidth] = useState(500);

  const MARGIN = 25;
  const PADDING = 8; //padding for the background

  const gotToCarouselItem = (index) => {
    const carousel = document.querySelector(".carousel_top");
    setPreviousCarouselIndex(index);
    if (carousel) {
      carousel.scrollBy({
        left: (imageWidth + MARGIN + PADDING * 2) * (index - previousCarouselIndex || index),
        top: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (!loading) {
      setCategories(formatCategories(data?.categories?.data || []));
      const index = Math.floor(formatCategories(data?.categories?.data || []).length / 2);
      setPreviousCarouselIndex(index);
    }
  }, [loading]);

  useEffect(() => {
    if (size && size.width) {
      setImageWidth(size.width / 2);
    }
  }, [size.width]);

  useEffect(() => {
    if (imageWidth && !isNaN(imageWidth) && MARGIN && previousCarouselIndex) {
      gotToCarouselItem(previousCarouselIndex);
    }
  }, [categories, imageWidth]);

  const handleNext = () => {
    if (document) {
      const carousel = document.querySelector(".carousel_top");
      if (
        carousel &&
        carousel.parentElement &&
        carousel.scrollLeft + carousel.parentElement.clientWidth < carousel.scrollWidth
      ) {
        carousel.scrollBy({
          left: imageWidth + MARGIN + PADDING * 2,
          top: 0,
          behavior: "smooth",
        });
        setPreviousCarouselIndex(previousCarouselIndex + 1);
      }
    }
  };

  const handlePrev = () => {
    if (document) {
      const carousel = document.querySelector(".carousel_top");

      if (carousel && carousel.scrollLeft != 0) {
        carousel.scrollBy({
          left: -(imageWidth + MARGIN + PADDING * 2),
          top: 0,
          behavior: "smooth",
        });
        setPreviousCarouselIndex(previousCarouselIndex - 1);
      }
    }
  };

  const debounceNextFn = useCallback(_debounce(handleNext, 200), [
    imageWidth,
    previousCarouselIndex,
  ]);

  const debouncePreviousFn = useCallback(_debounce(handlePrev, 200), [
    imageWidth,
    previousCarouselIndex,
  ]);

  return (
    <div className="relative" id="carousel-categories">
      <div
        id="carousel-categories"
        className="bg-white p-4 flex flex-col items-center scroll-mt-16"
      >
        <PageTitle hasBorder={false} title={translate("top_title_category")} />
        <span>{translate("must_visited_categories")} </span>
        <div className="flex w-[100%] items-center" dir="ltr">
          {/* max-w-min */}
          <div
            className={`flex snap-x snap-mandatory overflow-x-auto mt-4 w-full customScrollBar customCarouselScroll carousel_top xl:mx-auto mx-auto`}
            style={{
              gap: MARGIN,
              overflow: "hidden",
              position: "relative",
              width: "100%",
              whiteSpace: "nowrap",
            }}
          >
            <div
              className="carouselItem_top hero-card rounded-[55px]"
              style={{
                backgroundSize: "cover",
                minHeight: 200,
                minWidth: imageWidth / 2 - MARGIN - 32,
              }}
            ></div>
            {!loading
              ? Array.isArray(categories) &&
                categories?.map((item: any, index) => (
                  <Link key={index} href={`/categories/${item.slug}`} passHref legacyBehavior>
                    <div className="flex flex-col bg-backgroundGrey p-2 rounded-[55px] pb-5">
                      <div
                        className="carouselItem_top hero-card rounded-[55px] mb-2 cursor-pointer relative"
                        style={{
                          minHeight: 335.421,
                          minWidth: imageWidth,
                          boxShadow: "0 15px 6px -6px rgba(158, 147, 153, 0.23)",
                        }}
                      >
                        <BigCardThumbnail imageName={item.image.name} alt={item?.name} />
                        <div
                          className={`text-white text-3xl uppercase absolute top-6 ${direction === "ltr" ? "left-6" : "right-6"}`}
                        >
                          {categoryTranslate(categoryTranslateKey(item.slug))}
                        </div>
                      </div>
                      <TopStores categoryId={item.id} categorySlug={item.slug} itemsPerPage={4} />
                      {/* <button className="text-white font-bold bg-[#233377] w-[120px] h-[40.71px] rounded-full self-center my-2">
                        {translate("see_more")}
                      </button> */}
                    </div>
                  </Link>
                ))
              : Array.from(Array(7).keys()).map((item, index) => (
                  <Skeleton
                    key={`__index__carousel__card__${index}`}
                    containerClassName="w-96"
                    className="h-80"
                  />
                ))}
            <div
              className="carouselItem_top hero-card rounded-[55px]"
              style={{
                backgroundSize: "cover",
                minHeight: 200,
                minWidth: imageWidth / 2 - MARGIN,
              }}
            ></div>
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            top: "48%",
            display: "flex",
          }}
          dir="ltr"
        >
          {previousCarouselIndex ? (
            <button onClick={debouncePreviousFn}>
              <Image
                alt={translate("previousCategory")}
                src="/images/previousArrow.svg"
                width={ARROW_ICON_SIZE}
                height={ARROW_ICON_SIZE}
              />
            </button>
          ) : (
            <div className="w-[70px]"></div>
          )}
          <div
            style={{
              width: imageWidth - 30 - (MARGIN - PADDING) * 2,
              zIndex: -99,
            }}
          ></div>
          {previousCarouselIndex !== categories.length - 1 ? (
            <button onClick={debounceNextFn}>
              <Image
                alt={translate("nextCategory")}
                src="/images/nextArrow.svg"
                width={ARROW_ICON_SIZE}
                height={ARROW_ICON_SIZE}
              />
            </button>
          ) : (
            <div className="w-[70px]"></div>
          )}
        </div>
        <div className="flex rtl:flex-row-reverse mt-4">
          {!loading &&
            Array.isArray(categories) &&
            categories?.map((item, index) => (
              <button
                onClick={() => gotToCarouselItem(index)}
                key={`__carousel_indicator_${index}__`}
                type="button"
                className={`custom-space-x w-2 h-2 rounded-full ${index === previousCarouselIndex ? "bg-blue" : "bg-[#C6C6C6]"}`}
                aria-current="true"
                aria-label={`Slide ${index}`}
                data-carousel-slide-to={index}
              ></button>
            ))}
        </div>
      </div>
      <div className="bg-blue w-36 h-2.5 absolute right-0 rtl:left-0 rtl:right-auto"></div>
    </div>
  );
};

export default CarouselCategories;
